import { forwardRef } from "react";

interface PatternFirstCardProps {
  message: string;
  pattern: string;
  secondaryPattern?: string;
  interpretation: string;
  threatScore: number;
  emotionalHarmScore: number;
  signals?: string[];
}

const PatternFirstCard = forwardRef<HTMLDivElement, PatternFirstCardProps>(
  ({ message, pattern, secondaryPattern, interpretation, threatScore, emotionalHarmScore, signals }, ref) => {
    const isHigh = threatScore >= 7;
    const isMod = threatScore >= 4;
    const accentColor = isHigh ? "#ef4444" : isMod ? "#f59e0b" : "#64748b";

    return (
      <div
        ref={ref}
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: 1080,
          height: 1920,
          background: "#09090b",
          fontFamily: "'Inter', system-ui, sans-serif",
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: "80px 80px 64px",
        }}
      >
        {/* Subtle ambient glow behind pattern area */}
        <div
          style={{
            position: "absolute",
            top: "42%",
            left: "50%",
            width: 800,
            height: 800,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${accentColor}08 0%, transparent 65%)`,
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        />

        {/* ── 1. Top branding ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 64,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 14,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.50)",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            GREYROCKAI
          </span>
          <span
            style={{
              fontSize: 12,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.18)",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            Forensic Analysis
          </span>
        </div>

        {/* ── 2. Hook line ── */}
        <div
          style={{
            fontSize: 17,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.32)",
            fontFamily: "'JetBrains Mono', monospace",
            textAlign: "center",
            marginBottom: 48,
            flexShrink: 0,
          }}
        >
          Manipulation or misunderstanding?
        </div>

        {/* ── 3. Analyzed phrase ── */}
        <div
          style={{
            fontSize: 32,
            lineHeight: "48px",
            color: "rgba(255,255,255,0.88)",
            fontWeight: 300,
            fontStyle: "italic",
            textAlign: "center",
            maxWidth: 860,
            alignSelf: "center",
            marginBottom: 72,
            wordBreak: "break-word",
            flexShrink: 0,
          }}
        >
          "{message.slice(0, 220)}"
        </div>

        {/* ── Main payload (vertically centered) ── */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* 4. Pattern name — THE STAR */}
          <div
            style={{
              fontSize: 52,
              fontWeight: 800,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              color: "#ffffff",
              textAlign: "center",
              marginBottom: 32,
              lineHeight: 1.15,
              textShadow: isHigh
                ? `0 0 40px ${accentColor}30`
                : "none",
            }}
          >
            {pattern}
          </div>

          {/* Secondary pattern */}
          {secondaryPattern && (
            <div
              style={{
                fontSize: 20,
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.45)",
                textAlign: "center",
                marginBottom: 32,
              }}
            >
              Also detected: {secondaryPattern}
            </div>
          )}

          {/* Accent line */}
          <div
            style={{
              width: 48,
              height: 3,
              background: accentColor,
              borderRadius: 2,
              marginBottom: 32,
              opacity: 0.7,
            }}
          />

          {/* 5. One-line interpretation — THE MEANING */}
          <div
            style={{
              fontSize: 26,
              lineHeight: "42px",
              color: "rgba(255,255,255,0.72)",
              textAlign: "center",
              maxWidth: 800,
              fontWeight: 400,
              marginBottom: 52,
            }}
          >
            {interpretation}
          </div>

          {/* 6. Compact severity row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 48,
              marginBottom: signals && signals.length > 0 ? 40 : 0,
            }}
          >
            {[
              { label: "Threat Level", value: threatScore },
              { label: "Emotional Harm", value: emotionalHarmScore },
            ].map(({ label, value }) => {
              const c = value >= 7 ? "#ef4444" : value >= 4 ? "#f59e0b" : "#64748b";
              return (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 10,
                  }}
                >
                  <span
                    style={{
                      fontSize: 36,
                      fontWeight: 700,
                      color: c,
                      fontFamily: "'JetBrains Mono', monospace",
                      lineHeight: 1,
                    }}
                  >
                    {value.toFixed(1)}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.3)",
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* 7. Optional signal chips */}
          {signals && signals.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {signals.slice(0, 2).map((s, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: 14,
                    fontFamily: "'JetBrains Mono', monospace",
                    letterSpacing: "0.04em",
                    color: "rgba(0,242,255,0.7)",
                    padding: "8px 20px",
                    borderRadius: 20,
                    background: "rgba(0,242,255,0.05)",
                    border: "1px solid rgba(0,242,255,0.15)",
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── 8. Footer branding ── */}
        <div
          style={{
            fontSize: 15,
            letterSpacing: "0.18em",
            color: "rgba(255,255,255,0.60)",
            fontWeight: 500,
            textAlign: "center",
            flexShrink: 0,
          }}
        >
          ANALYZE YOURS → GREYROCKAI.APP®
        </div>
      </div>
    );
  }
);

PatternFirstCard.displayName = "PatternFirstCard";
export default PatternFirstCard;
